/*
 * VatomInc. Copyright (c) 2022, all rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in the root directory of this source tree.
 */

import { Platform } from 'react-native'
import DeviceInfo from 'react-native-device-info'
import {
  REACT_APP_SENTRY_DSN,
  REACT_APP_SENTRY_ENV,
  REACT_APP_SENTRY_TRACES_SAMPLE_RATE,
  STAGE
} from 'react-native-dotenv'
import {  browserTracingIntegration } from '@sentry/react'
import type { ReactNativeOptions } from '@sentry/react-native'
import type { AxiosAdapter, AxiosError, AxiosRequestConfig } from 'axios'
import createDebug from 'debug'
import * as Device from 'expo-device'

import { Sentry } from './sentry'

export const instance = Sentry

const debug = createDebug('sentry')

// https://docs.sentry.io/platforms/javascript/performance/instrumentation/automatic-instrumentation/
// Defined at this scope so we can access options in `historyListener`.
const browserTracing = browserTracingIntegration({
  // For some reason the built-in fetch / xhr
  // overloading functionality of this integration
  // would stop working after a `react-router` page
  // navigation. Requests would have the
  // "sentry-trace" header missing. At least, I
  // noticed this with our api requests via `axios`.
  // To handle this, I wrote a custom `axios`
  // adapter.
  traceFetch: false,
  traceXHR: false

  // We will also allow the default `pagelaod` and `navigation` events. These
  // events don't respond to `react-router` navigation changes, so we'll also
  // add our own history listener via the `RouteWatcher` React component.
  //
  // I think that `react-router` calls the `useSWR` hooks before
  // `history.listen`, so some requests don't get tracked as spans on
  // "navigation" events.
})

/**
 * Returns an axios adapter which wraps an existing adapter and which also ships
 * requests to Sentry as traces.
 * This approach of wrapping another adapter works basically like "middleware"
 * in other codebases, though axios doesn't use that terminology.
 *
 * https://docs.sentry.io/platforms/javascript/performance/instrumentation/custom-instrumentation
 *
 * Based on `@sentry/tracing` source code for `fetch` integration:
 *  Repo: https://github.com/getsentry/sentry-javascript
 *  Commit: f96d47b8b53d8a9301981a74a75d98aaca8a71e0
 *  File: packages/tracing/src/browser/request.ts
 *  Function: fetchCallback
 */
export const axiosMiddleware =
  (next: AxiosAdapter) =>
  async (config: AxiosRequestConfig): Promise<any> => {
    const method = config.method?.toUpperCase()
    const url = (config.baseURL ?? '') + (config.url ?? '')

    // Get the current transaction (if using `BrowserTracing` integration).
    const tx = Sentry.getCurrentHub()?.getScope()?.getTransaction()

    // If no transaction is active, then skip tracing.
    // This means that axios requests which occur after a "pageload" or
    // "navigation" will not be traced. This appears to be the standard
    // behavior of their SDK.
    if (tx === undefined) {
      debug(`Skipping axios req trace for ${method} ${url} - no active tx.`)
      // @ts-ignore
      return next(config)
    }

    // TODO(sentry): Use `startInactiveSpan()` instead - see https://github.com/getsentry/sentry-javascript/blob/develop/docs/v8-new-performance-apis.md
    const span = tx.startChild({
      op: 'http.client',
      description: `${method} ${url}`,
      data: {
        type: 'axios',
        method,
        url
      }
    })

    // Add the `sentry-trace` header to be used to continue instrumenting the
    // transaction on the backend.
    const traceparent = tx.toTraceparent()
    // eslint-disable-next-line no-param-reassign
    config.headers!['sentry-trace'] = traceparent

    debug(`Tracing axios req for ${method} ${url} - ${traceparent}`)

    // Call the original adapter to actually make the request. This will *not*
    // throw on "error status codes" (by default 2xx, but configurable by
    // request). That's good, because that means we will ship these failed
    // requests to Sentry just the same as successful ones.
    // @ts-ignore
    const res = await next(config)

    span.setData('method', config.method?.toUpperCase())
    span.setData('url', url)
    span.setData('status_code', res.status)

    span.finish()
    return res
  }

/**
 * Custom pre-processing for Sentry events.
 */
function beforeSend(event: Sentry.Event, originalException: any) {
  //   const ex = hint?.originalException

  // Axios Exceptions

  if (typeof originalException === 'object' && (originalException as any)?.isAxiosError) {
    const axEx = originalException as AxiosError

    const { config = {} } = axEx
    // eslint-disable-next-line prefer-template
    // @ts-ignore
    const url = config.baseURL ?? '' + config.url ?? ''
    // @ts-ignore
    const { authorization = '' } = config.headers ?? {}
    const authScheme =
      typeof authorization === 'string' ? authorization.split(' ')[0] : authorization

    // eslint-disable-next-line no-param-reassign
    event.contexts = {
      ...event.contexts,
      request: {
        // @ts-ignore
        method: config.method,
        url,
        authScheme
      },
      response: {
        status: axEx.response?.status,
        data: axEx.response?.data
      }
    }
  }

  return event
}

export const captureError = (e: Error) => {
  // https://github.com/getsentry/sentry-javascript/issues/2546
  // https://stackoverflow.com/questions/44815172/log-shows-error-object-istrustedtrue-instead-of-actual-error-data
  // eslint-disable-next-line no-prototype-builtins
  if (e && e.hasOwnProperty('isTrusted')) {
    // Sentry.captureException(new Error(e.message))
    // Ignore for now
  } else {
    Sentry.captureException(e, { contexts: { error: e as any } })
  }
}

export const captureMessage = Sentry.captureMessage

/**
 * Initialize Sentry support.
 * Should be done as early as possible in execution.
 */
export const init = () => {
  // logger.info('[Sentry.init]')
  // Disable Sentry support if env var unset or empty.
  if (REACT_APP_SENTRY_DSN === undefined || REACT_APP_SENTRY_DSN === '') {
    // logger.warn('[Sentry.init] missing env')
    debug('Disabled')
    return
  }

  let sampleRate = Number.parseFloat(REACT_APP_SENTRY_TRACES_SAMPLE_RATE ?? '')

  // By default, sample all traces.
  if (Number.isNaN(sampleRate)) {
    sampleRate = 1.0
  }

  const isNative = Platform.OS !== 'web'
  const enabled = Device.isDevice
  const release = DeviceInfo.getVersion()
  const isDev = STAGE === 'dev'
  let environment = REACT_APP_SENTRY_ENV || STAGE === 'prod' ? 'production' : 'development'

  if (Platform.OS === 'web' && window.origin) {
    switch (window.location.host) {
      case 'wallet.vatom.com':
        environment = 'production'
        break
      case 'wallet.vatominc.com':
        environment = 'staging'
        break
      default:
        environment = 'development'
    }
  }

  const sentryOptions: ReactNativeOptions = {
    dsn: REACT_APP_SENTRY_DSN,
    attachThreads: isDev,
    autoInitializeNativeSdk: isNative,
    attachStacktrace: true,
    attachScreenshot: isDev,
    enabled,

    // @ts-expect-error not existing enableAutoPerformanceTracking?
    enableAutoPerformanceTracking: isDev,
    enableAutoSessionTracking: true,
    enableOutOfMemoryTracking: isDev,
    enableNativeCrashHandling: true,
    enableNative: isNative,
    enableNdkScopeSync: true,
    environment,
    release,
    ...(isNative ? null : { integrations: [browserTracing] }),
    tracesSampleRate: sampleRate,
    beforeSend,
    debug: false // Toggle to enable more logging for issue diagnosis.
  }

  // logger.info('[Sentry.init] sentryOptions', sentryOptions)
  Sentry.init(sentryOptions)

  debug('Inited')
}
